// frontend/libs/dsm/components/src/lib/molecules/certificate-viewer.tsx

import React from 'react';
import styled from '@emotion/styled';
import { Certificate } from '@taleemabad/db';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const CertificateContent = styled.div`
  padding: 40px;
  text-align: center;
`;

const CertificateContainer = styled.div`
  border: 3px solid #003366;
  border-radius: 8px;
  padding: 40px;
  background: linear-gradient(to bottom, #fafafa, #f0f0f0);
  margin: 20px 0;
  font-family: 'Georgia', serif;
  color: #333;
`;

const CertificateTitle = styled.h1`
  margin: 0 0 20px 0;
  font-size: 32px;
  color: #003366;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const CertificateText = styled.p`
  margin: 12px 0;
  font-size: 16px;
  line-height: 1.6;
`;

const TeacherName = styled.p`
  margin: 30px 0;
  font-size: 20px;
  font-weight: bold;
  color: #003366;
`;

const TrainingTitle = styled.p`
  margin: 20px 0;
  font-size: 18px;
  font-style: italic;
  color: #555;
`;

const CertificateCode = styled.p`
  margin: 30px 0 0 0;
  font-size: 12px;
  color: #999;
  font-family: monospace;
`;

const Signatory = styled.div`
  margin-top: 40px;
  text-align: center;
`;

const SignatoryName = styled.p`
  margin: 20px 0 5px 0;
  font-weight: bold;
  color: #333;
`;

const SignatoryTitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const ActionButtons = styled.div`
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: #4caf50;
  color: white;

  &:hover {
    background: #45a049;
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: #f5f5f5;
  color: #333;
  border: 1px solid #e0e0e0;

  &:hover {
    background: #efefef;
  }
`;

export interface CertificateViewerProps {
  certificate: Certificate;
  onClose: () => void;
  onDownload?: () => void;
}

export function CertificateViewer({
  certificate,
  onClose,
  onDownload,
}: CertificateViewerProps) {
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      window.print();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Certificate of Completion</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close certificate viewer">
            &times;
          </CloseButton>
        </ModalHeader>

        <CertificateContent>
          <CertificateContainer
            style={
              certificate.vendor === 'OXBRIDGE'
                ? {
                    borderColor:
                      certificate.metadata.colors?.primary || '#003366',
                    borderWidth: '3px',
                    outline: `6px solid ${
                      certificate.metadata.colors?.secondary || '#FFD700'
                    }`,
                    outlineOffset: '-10px',
                  }
                : undefined
            }
          >
            <CertificateTitle>Certificate of Completion</CertificateTitle>

            <CertificateText>This certifies that</CertificateText>
            <TeacherName>{certificate.teacherName}</TeacherName>

            <CertificateText>
              has successfully completed the course:
            </CertificateText>
            <TrainingTitle>{certificate.trainingName}</TrainingTitle>

            <CertificateText>
              Date of Completion: <strong>{certificate.completionDate}</strong>
            </CertificateText>

            <Signatory>
              <SignatoryName>{certificate.metadata.signatory}</SignatoryName>
              <SignatoryTitle>{certificate.metadata.title}</SignatoryTitle>
              <SignatoryTitle>{certificate.metadata.issuer}</SignatoryTitle>
            </Signatory>

            <CertificateCode>
              Certificate No: {certificate.certificateCode}
            </CertificateCode>
          </CertificateContainer>
        </CertificateContent>

        <ActionButtons>
          <PrimaryButton onClick={handleDownload}>
            Download as PDF
          </PrimaryButton>
          <SecondaryButton onClick={handlePrint}>Print</SecondaryButton>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </ActionButtons>
      </ModalContent>
    </ModalOverlay>
  );
}

export default CertificateViewer;
